import { useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';

import { useNavigation } from '../../hooks/useNavigation';

import './menu.css';

interface NavigationMenuProps {
  setIsMenuOpen: (isOpen: boolean) => void;
  setMenuAnimTrigger: (isOpen: boolean) => void;
  animation: 'in' | 'out';
}

export default function NavigationMenu({ setIsMenuOpen, setMenuAnimTrigger, animation }: NavigationMenuProps) {
    const { goLanding, goAbout, goProjects, goStories } = useNavigation();
    const location = useLocation();
    const currentPath = location.pathname;
    const isActive = (path: string) => currentPath === path;

    const handleNavClick = (toLocation: () => void) => {
        toLocation();
        setTimeout(() => {
            setMenuAnimTrigger(false);
            setIsMenuOpen(false);
        }, 300);
    };

    const handleLinkClick = (url: string) => {
        window.open(url, '_blank', 'noopener,noreferrer');
    }

    const handleLandingClick = () => handleNavClick(goLanding);
    const handleAboutClick = () => handleNavClick(goAbout);
    const handleProjectsClick = () => handleNavClick(goProjects);
    const handleStoriesClick = () => handleNavClick(goStories);
    const handleBookClick = () => handleLinkClick('https://outlook.office365.com/book/BroadcastStudioBookings@broadcaststudio.com');
    const handleEmailClick = () => handleLinkClick('mailto:info@broadcaststudio.com');
    const handleWebsiteClick = () => handleLinkClick('https://www.broadcaststudio.com/');

    useEffect(() => {
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = originalOverflow;
        };
    }, []);

    return (
        <Box
            className={animation === 'in' ? 'animate-in' : 'animate-out'}
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                backgroundColor: 'rgb(17, 17, 17)',
                zIndex: 1200,
                overflowY: 'auto',
                display: 'flex',
                justifyContent: 'center',
                pt: { xs: 8, md: 12 },
                px: 2,
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    maxWidth: '1200px',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: { xs: 6, md: 0 },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        width: '100%',
                        maxWidth: { xs: '100vw', md: '50%' },
                        marginBottom: { sx: '-8px', md: 0},
                        px: { xs: 3, md: 0 },
                        py: { xs: 2, md: 0 },
                        mt: { xs: 6, md: 0 },
                    }}
                    className="nav-dropdown-btns"
                >
                    <button 
                        className={`nav-dropdown-btn ${isActive('/') ? 'nav-dropdown-btns-active' : ''}`} 
                        onClick={handleLandingClick}
                    >
                        Home
                    </button>
                    <button 
                        className={`nav-dropdown-btn ${isActive('/about') ? 'nav-dropdown-btns-active' : ''}`}
                        onClick={handleAboutClick}
                    >
                        About
                    </button>
                    <button 
                        className={`nav-dropdown-btn ${isActive('/projects') ? 'nav-dropdown-btns-active' : ''}`} 
                        onClick={handleProjectsClick}
                    >
                    Projects
                    </button>
                    <button
                        className={`nav-dropdown-btn ${isActive('/stories') ? 'nav-dropdown-btns-active' : ''}`}
                        onClick={handleStoriesClick}
                    >
                        Stories
                    </button>
                    <button
                        className="nav-dropdown-btn tab-indictor"
                        onClick={handleBookClick}
                    >
                        Book
                    </button>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: { xs: 'flex-start', md: 'flex-end' },
                        justifyContent: { xs: 'center', md: 'flex-end' },
                        width: '100%',
                        maxWidth: { xs: '100vw', md: '45%' },
                        gap: 2,
                        px: { xs: 3, md: 0 },
                        py: { xs: 2, md: 0 },
                        mt: { xs: 4, md: 48 },
                        color: 'white',
                    }}
                >
                    <Box>
                        <Typography sx={{ fontSize: { xs: '16px', md: '22px' }, maxWidth: '200px', lineHeight: '1.8' }}>
                            426 South Lamar Blvd.Oxford, MS 38655
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            flexWrap: 'nowrap',
                            alignItems: 'flex-start',
                            justifyContent: 'flex-start',
                            gap: '18px',
                            height: 'min-content',
                            overflow: 'visible',
                            padding: '0 0 10px',
                            position: 'relative',
                            width: 'min-content',
                            textAlign: 'right',
                            ml: {xs: 0, md: 5},
                            mb: {xs: 0, md: 2},
                        }}
                    >
                        <Typography sx={{fontSize: { xs: '16px', md: '22px' }}} className="underlined-text" onClick={handleEmailClick}>info@broadcaststudio.com</Typography>
                        <Typography sx={{fontSize: { xs: '16px', md: '22px' }}} className="underlined-text" onClick={handleWebsiteClick}>broadcaststudio.com</Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
